// src/components/ExportButton.tsx
import React, { useState } from 'react';
import { Download, FileSpreadsheet, ChevronDown, Printer } from 'lucide-react';

interface Props {
  module: string;
  label?: string;
  params?: Record<string, string>;
}

const ExportButton: React.FC<Props> = ({ module, label = 'Экспорт', params }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  // ★ URL в зависимости от модуля
  const getExportUrl = () => {
    const base = module === 'deals' ? '/api/sales/deals/export' : `/api/accounting/${module}/export`;
    if (!params || !Object.keys(params).length) return base;
    const qs = new URLSearchParams(params).toString();
    return qs ? `${base}?${qs}` : base;
  };

  const handleExcel = async () => {
    setLoading(true); setOpen(false);
    try {
      const r = await fetch(getExportUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`Ошибка ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `${module}.xlsx`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      alert('Ошибка экспорта Excel: ' + e.message);
    } finally { setLoading(false); }
  };

  const handlePrint = async () => {
    setLoading(true); setOpen(false);
    try {
      const r = await fetch(getExportUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`Ошибка ${r.status}`);
      const buf = await r.arrayBuffer();

      const XLSX = await import('xlsx');
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (!rows.length) throw new Error('Нет данных');

      const headers = rows[0] as string[];
      const body = rows.slice(1) as any[][];
      const titleMap: Record<string, string> = {
        accounts: 'План счетов', counterparties: 'Контрагенты',
        invoices: 'Счета', payments: 'Платежи',
        operations: 'Операции', documents: 'Документы',
      };
      const title = titleMap[module] || 'Отчёт';
      const dateStr = new Date().toLocaleDateString('ru-RU');

      const html = `<!doctype html><html lang="ru"><head><meta charset="utf-8">
<title>${title} — ${dateStr}</title>
<style>
@page{size:A4 landscape;margin:12mm}
body{font-family:-apple-system,"Segoe UI",Roboto,Arial,sans-serif;color:#1a1a2e;margin:0;padding:12px}
h1{font-size:20px;margin:0 0 4px}
.meta{font-size:12px;color:#64748b;margin-bottom:14px}
table{border-collapse:collapse;width:100%;font-size:11px}
thead th{background:#f1f5f9;text-align:left;padding:8px 10px;border-bottom:2px solid #cbd5e1;font-weight:700}
tbody td{padding:6px 10px;border-bottom:1px solid #e2e8f0}
tbody tr:nth-child(even){background:#fafbfc}
.num{text-align:right;font-variant-numeric:tabular-nums}
@media print{thead{display:table-header-group}tr{page-break-inside:avoid}}
</style></head><body>
<h1>${title}</h1>
<div class="meta">Выгружено ${dateStr} · Всего записей: ${body.length}</div>
<table><thead><tr>${headers.map(h => `<th>${h || ''}</th>`).join('')}</tr></thead>
<tbody>${body.map(row => '<tr>' + headers.map((_, i) => {
  const v = row[i] ?? '';
  const isNum = typeof v === 'number' || /^-?[\d\s]+,?\d*$/.test(String(v));
  return `<td class="${isNum ? 'num' : ''}">${String(v)}</td>`;
}).join('') + '</tr>').join('')}</tbody></table>
<script>window.onload=()=>{setTimeout(()=>window.print(),300);};</script>
</body></html>`;

      const w = window.open('', '_blank');
      if (!w) { alert('Разрешите всплывающие окна'); return; }
      w.document.write(html);
      w.document.close();
    } catch (e: any) {
      alert('Ошибка печати PDF: ' + e.message);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} disabled={loading} style={btnStyle}>
        <Download size={14} />
        {loading ? 'Экспорт...' : label}
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
          <div style={menuBox}>
            <button onClick={handleExcel} style={menuItem}>
              <FileSpreadsheet size={14} color="#10b981" />
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontWeight: 600 }}>Excel (.xlsx)</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>Скачать файл</div>
              </div>
            </button>
            <button onClick={handlePrint} style={menuItem}>
              <Printer size={14} color="#2575fc" />
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontWeight: 600 }}>PDF / Печать</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>Открыть печатную форму</div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const btnStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.8)',
  borderRadius: 12, padding: '10px 16px', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 13, fontWeight: 600, color: '#475569', fontFamily: 'inherit',
};
const menuBox: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 4px)', right: 0,
  background: '#fff', borderRadius: 12, padding: 6,
  boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
  zIndex: 51, minWidth: 220,
};
const menuItem: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: 'none',
  background: 'transparent', cursor: 'pointer',
  fontSize: 13, color: '#1a1a2e', borderRadius: 8,
  display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'inherit',
};

export default ExportButton;
